"use client";

import {
	FaFacebookF,
	FaInstagram,
	FaLinkedin,
	FaTwitter,
} from "react-icons/fa6";
import { MdMail, MdPhone } from "react-icons/md";
import Link from "next/link";
import { motion, AnimatePresence } from "framer-motion";
import { Input } from "@/components/ui/input";
import { MagneticButton } from "@/components/custom/magnetic-button";
import { useState, useEffect, FormEvent } from "react";
import { Loader2, CheckCircle, AlertCircle, X } from "lucide-react";

const containerVariants = {
	hidden: { opacity: 0 },
	visible: {
		opacity: 1,
		transition: {
			staggerChildren: 0.1,
			delayChildren: 0.2,
		},
	},
};

const itemVariants = {
	hidden: { y: 20, opacity: 0 },
	visible: {
		y: 0,
		opacity: 1,
		transition: {
			duration: 0.5,
			ease: [0.22, 1, 0.36, 1] as [number, number, number, number],
		},
	},
};

export default function Footer() {
	const [email, setEmail] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [submitStatus, setSubmitStatus] = useState<{ type: "success" | "error"; message: string } | null>(null);

	// Auto-dismiss notification after 7 seconds
	useEffect(() => {
		if (submitStatus) {
			const timer = setTimeout(() => {
				setSubmitStatus(null);
			}, 7000);
			return () => clearTimeout(timer);
		}
	}, [submitStatus]);

	const handleNewsletterSubmit = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setIsSubmitting(true);
		setSubmitStatus(null);

		try {
			const response = await fetch("/api/newsletter", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ email }),
			});

			const data = await response.json();

			if (response.ok) {
				setSubmitStatus({ type: "success", message: data.message });
				setEmail("");
			} else {
				setSubmitStatus({ type: "error", message: data.error || "Something went wrong" });
			}
		} catch {
			setSubmitStatus({ type: "error", message: "Failed to subscribe. Please try again." });
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<footer className="relative z-10 bg-zinc-950 text-white">
			{/* Top Border */}
			<div className="h-px w-full bg-zinc-800" />

			<motion.div
				className="mx-auto max-w-[1920px]"
				initial="hidden"
				whileInView="visible"
				viewport={{ once: true, margin: "-100px" }}
				variants={containerVariants}
			>
				{/* Main Grid Layout */}
				<div className="grid grid-cols-1 md:grid-cols-12 border-b border-zinc-800">
					{/* Brand Section - Spans 5 columns */}
					<div className="col-span-1 md:col-span-5 border-r border-zinc-800 px-4 pt-12 pb-8 md:p-10 flex flex-col justify-between min-h-[400px]">
						<motion.div variants={itemVariants}>
							<h2 className="font-syne text-5xl md:text-7xl font-bold uppercase leading-[0.9] tracking-tighter mb-6">
								The
								<br />
								Highrays
							</h2>
							<p className="font-inter text-zinc-400 max-w-sm text-lg leading-relaxed">
								Building digital success stories through innovative solutions and specialized services for businesses and creators.
							</p>
						</motion.div>

						<motion.div variants={itemVariants} className="mt-12">
							<div className="flex gap-4" role="list" aria-label="Social media links">
								{[
									{ Icon: FaFacebookF, label: "Follow us on Facebook", href: "#" },
									{ Icon: FaTwitter, label: "Follow us on Twitter", href: "#" },
									{ Icon: FaLinkedin, label: "Connect with us on LinkedIn", href: "#" },
									{ Icon: FaInstagram, label: "Follow us on Instagram", href: "#" },
								].map(({ Icon, label, href }, i) => (
									<a
										key={i}
										href={href}
										aria-label={label}
										className="group flex h-12 w-12 items-center justify-center bg-zinc-950 transition-all hover:bg-white hover:text-black"
									>
										<Icon size={20} aria-hidden="true" />
									</a>
								))}
							</div>
						</motion.div>
					</div>

					{/* Middle Section - Spans 3 columns */}
					<div className="col-span-1 md:col-span-3 border-r border-zinc-800">
						<div className="flex flex-col h-full">
							{/* Quick Links */}
							<div className="px-4 pt-12 pb-8 md:p-10 border-b border-zinc-800">
								<motion.h3
									variants={itemVariants}
									className="mb-6 font-syne text-sm font-bold uppercase tracking-widest text-zinc-500"
								>
									Explore
								</motion.h3>
								<ul className="space-y-4 font-syne text-xl font-bold uppercase">
									{[
										{ name: "About Us", href: "/about" },
										{ name: "Contact", href: "/contact" },
										{ name: "Privacy Policy", href: "/privacy-policy" },
										{ name: "Terms & Conditions", href: "/terms-and-conditions" },
									].map((link) => (
										<motion.li key={link.href} variants={itemVariants}>
											<Link
												href={link.href}
												className="inline-block transition-transform hover:translate-x-2 text-zinc-300 hover:text-white"
											>
												{link.name}
											</Link>
										</motion.li>
									))}
								</ul>
							</div>

							{/* Contact Info */}
							<div className="px-4 pt-12 pb-8 md:p-10 flex flex-col justify-center flex-1">
								<motion.h3
									variants={itemVariants}
									className="mb-6 font-syne text-sm font-bold uppercase tracking-widest text-zinc-500"
								>
									Get in Touch
								</motion.h3>
								<motion.div variants={itemVariants} className="space-y-8">
									{/* Canada Office */}
									<div className="space-y-4">
										<div className="font-mono text-xs text-zinc-300 uppercase tracking-widest mb-2">
											North America (Ottawa)
										</div>
										<a
											href="tel:+14167264766"
											className="flex items-center gap-4 group"
										>
											<div className="flex h-10 w-10 items-center justify-center transition-colors group-hover:bg-white group-hover:text-black shrink-0">
												<MdPhone size={20} />
											</div>
											<span className="font-inter text-zinc-300 group-hover:text-white transition-colors">
												+1 416 726 4766
											</span>
										</a>
									</div>

									{/* India Office */}
									<div className="space-y-4">
										<div className="font-mono text-xs text-zinc-500 uppercase tracking-widest mb-2">
											South Asia (India, HQ)
										</div>
										<a
											href="tel:+918273262260"
											className="flex items-center gap-4 group"
										>
											<div className="flex h-10 w-10 items-center justify-center transition-colors group-hover:bg-white group-hover:text-black shrink-0">
												<MdPhone size={20} />
											</div>
											<span className="font-inter text-zinc-300 group-hover:text-white transition-colors">
												+91 8273262260
											</span>
										</a>
										<a
											href="mailto:info@thehighrays.com"
											className="flex items-center gap-4 group"
										>
											<div className="flex h-10 w-10 items-center justify-center transition-colors group-hover:bg-white group-hover:text-black shrink-0">
												<MdMail size={20} />
											</div>
											<span className="font-inter text-zinc-300 group-hover:text-white transition-colors">
												info@thehighrays.com
											</span>
										</a>
									</div>
								</motion.div>
							</div>
						</div>
					</div>

					{/* Newsletter Section - Spans 4 columns */}
					<div className="col-span-1 md:col-span-4 px-4 pt-12 pb-8 md:p-10 flex flex-col justify-center bg-zinc-900/30">
						<motion.div variants={itemVariants}>
							<h3 className="mb-2 font-syne text-3xl font-bold uppercase text-white">
								Stay in the loop
							</h3>
							<p className="mb-8 font-inter text-zinc-400">
								Subscribe to our newsletter for the latest updates and insights.
							</p>
							<form onSubmit={handleNewsletterSubmit} className="space-y-4">
								<Input
									type="email"
									placeholder="EMAIL ADDRESS"
									value={email}
									onChange={(e) => setEmail(e.target.value)}
									disabled={isSubmitting}
									required
									className="h-14 rounded-none border-zinc-700 bg-transparent px-6 font-syne text-lg uppercase tracking-wide text-white placeholder:text-zinc-600 focus:border-white focus:ring-0 disabled:opacity-50"
								/>
								<AnimatePresence mode="wait">
									{submitStatus && (
										<motion.div
											initial={{ opacity: 0, y: -10, scale: 0.95 }}
											animate={{ opacity: 1, y: 0, scale: 1 }}
											exit={{ opacity: 0, y: -5, scale: 0.95 }}
											transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
											className={`relative overflow-hidden ${submitStatus.type === "success"
													? "bg-zinc-800/50 border-l-2 border-l-green-500"
													: "bg-zinc-800/50 border-l-2 border-l-red-500"
												}`}
										>
											{/* Progress bar for auto-dismiss */}
											<motion.div
												initial={{ width: "100%" }}
												animate={{ width: "0%" }}
												transition={{ duration: 7, ease: "linear" }}
												className={`absolute bottom-0 left-0 h-0.5 ${submitStatus.type === "success" ? "bg-green-500/40" : "bg-red-500/40"
													}`}
											/>
											<div className="p-3 flex items-center justify-between gap-3">
												<div className="flex items-center gap-3">
													{submitStatus.type === "success" ? (
														<CheckCircle className="h-4 w-4 text-green-400 flex-shrink-0" />
													) : (
														<AlertCircle className="h-4 w-4 text-red-400 flex-shrink-0" />
													)}
													<span className={`font-inter text-sm ${submitStatus.type === "success" ? "text-green-400" : "text-red-400"
														}`}>
														{submitStatus.message}
													</span>
												</div>
												<button
													onClick={() => setSubmitStatus(null)}
													className="text-zinc-500 hover:text-white transition-colors p-0.5 flex-shrink-0"
													aria-label="Dismiss notification"
												>
													<X className="h-3.5 w-3.5" />
												</button>
											</div>
										</motion.div>
									)}
								</AnimatePresence>
								<MagneticButton
									type="submit"
									disabled={isSubmitting}
									className="h-14 w-full rounded-none bg-white px-8 font-syne text-lg font-bold uppercase text-black hover:bg-zinc-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
								>
									{isSubmitting ? (
										<span className="flex items-center justify-center gap-2">
											<Loader2 className="h-5 w-5 animate-spin" />
											Subscribing...
										</span>
									) : (
										"Subscribe"
									)}
								</MagneticButton>
							</form>
						</motion.div>
					</div>
				</div>

				{/* Footer Bottom */}
				<div className="flex flex-col md:flex-row justify-between items-center p-4 md:p-10 text-xs font-inter text-zinc-500 uppercase tracking-widest">
					<motion.p variants={itemVariants}>
						© {new Date().getFullYear()} TheHighrays
					</motion.p>
					<motion.div variants={itemVariants} className="flex flex-col md:flex-row items-center gap-2 md:gap-8 mt-4 md:mt-0 max-sm:text-[10px]">
						<span className="text-center">All rights reserved</span>
						<span className="text-center">Designed with Precision</span>
					</motion.div>
				</div>
			</motion.div>
		</footer>
	);
}
